import React, { useState, useEffect } from 'react';
import { requestLocationPermission } from '../utils/geolocation';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MapPin, X, RefreshCw } from 'lucide-react';

const LocationPermission = ({ onLocationObtained, onLocationDenied }) => {
  const [hasRequested, setHasRequested] = useState(false);
  const [showMandatoryPopup, setShowMandatoryPopup] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    
    // Prevent duplicate mounting
    if (window.locationPermissionMounted) {
      return;
    }
    window.locationPermissionMounted = true;
    
    // Check if location request is already in progress globally
    if (window.locationRequestInProgress) {
      return;
    }
    
    // Check if we've already requested location permission today
    const lastRequestDate = localStorage.getItem('locationRequestDate');
    const today = new Date().toDateString();
    
    // Always check location permission status, regardless of when we last requested
    checkLocationPermissionStatus();
    
    // Cleanup on unmount
    return () => {
      window.locationPermissionMounted = false;
    };
  }, []);

  const checkIfPermissionWasDenied = async () => {
    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'denied') {
          setShowMandatoryPopup(true);
        } else if (permission.state === 'granted') {
          await getCurrentLocation();
        } else {
          // Don't show popup for prompt state, let the normal flow handle it
        }
      }
    } catch (error) {
      // Could not check permission state
    }
  };

  const checkLocationPermissionStatus = async () => {
    if (hasRequested) {
      return;
    }
    
    // Set global flag to prevent multiple requests
    window.locationRequestInProgress = true;
    setHasRequested(true);
    
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        return;
      }

      // Check current permission status
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        
        if (permission.state === 'granted') {
          // Get current location since permission is already granted
          await getCurrentLocation();
          return;
        } else if (permission.state === 'denied') {
          // Show mandatory popup for denied permission
          setShowMandatoryPopup(true);
          return;
        }
        // If 'prompt', we'll request permission below
      } else {
        // Permissions API not available, will try to request permission
      }
      
      // If permission is 'prompt' or permissions API not available, request permission
      
      // Clear the global flag before making the request
      window.locationRequestInProgress = false;
      await requestLocationAutomatically();
      
    } catch (error) {
      // Fallback: try to request permission anyway
      await requestLocationAutomatically();
    } finally {
      // Clear global flag
      window.locationRequestInProgress = false;
    }
  };

  const getCurrentLocation = async () => {
    try {
      // Prevent multiple simultaneous requests
      if (window.locationRequestInProgress) {
        return;
      }
      
      window.locationRequestInProgress = true;
      
      const location = await requestLocationPermission();
      
      const locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        location_name: `Lat: ${location.latitude.toFixed(4)}, Lon: ${location.longitude.toFixed(4)}`
      };
      
      
      // Send location to backend
      await sendLocationToBackend(locationData);
      
      if (onLocationObtained) {
        onLocationObtained(locationData);
      }
      
    } catch (error) {
      // Failed to get current location
    } finally {
      window.locationRequestInProgress = false;
    }
  };

  const requestLocationAutomatically = async () => {
    try {
      // Prevent multiple simultaneous requests
      if (window.locationRequestInProgress) {
        return;
      }
      
      window.locationRequestInProgress = true;
      
      const location = await requestLocationPermission();
      
      const locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        location_name: `Lat: ${location.latitude.toFixed(4)}, Lon: ${location.longitude.toFixed(4)}`
      };
      
      
      // Send location to backend
      await sendLocationToBackend(locationData);
      
      // Store that we've requested location today
      localStorage.setItem('locationRequestDate', new Date().toDateString());
      
      if (onLocationObtained) {
        onLocationObtained(locationData);
      }
      
    } catch (error) {
      // Store that we've requested location today (even if denied)
      localStorage.setItem('locationRequestDate', new Date().toDateString());
      
      // Wait a moment to see if this is a real denial or just a temporary issue
      
      // Wait a moment and check permission state again
      setTimeout(async () => {
        try {
          if (navigator.permissions) {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            
            if (permission.state === 'denied') {
              setShowMandatoryPopup(true);
            } else {
              // Permission is not denied, might be temporary issue - not showing popup
            }
          } else {
            // Fallback: show popup if we can't check permission state
            setShowMandatoryPopup(true);
          }
        } catch (e) {
          setShowMandatoryPopup(true);
        }
      }, 1000); // Wait 1 second before checking
      
      if (onLocationDenied) {
        onLocationDenied(error);
      }
    } finally {
      window.locationRequestInProgress = false;
    }
  };

  const sendLocationToBackend = async (locationData) => {
    try {
      await axios.post('/api/users/update-location', locationData);
      toast.success('Location tracking enabled!');
    } catch (error) {
      // Failed to send location to backend
    }
  };

  const retryLocationRequest = async () => {
    setIsRetrying(true);
    
    // Show loading message
    toast.loading('Refreshing page...', { duration: 1000 });
    
    // Refresh the page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Show mandatory popup if location is denied
  if (showMandatoryPopup) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <MapPin className="h-8 w-8 text-red-600" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Location Permission Required
          </h3>
          
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="text-xs font-medium text-blue-800 mb-2">How to enable location:</h4>
            <ol className="text-xs text-blue-700 space-y-1">
              <li>1. Click the "Refresh" button below</li>
              <li>2. Allow location access when prompted</li>
              <li>3. Page will refresh and location will be enabled</li>
            </ol>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={retryLocationRequest}
              disabled={isRetrying}
              className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Refreshing Page...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </>
              )}
            </button>
          </div>
          
          
        </div>
      </div>
    );
  }

  // Don't render anything if location is granted or not yet requested
  return null;
};

export default LocationPermission;
